
import React, { useCallback, useState, useMemo } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageContainer, ContentWrapper, GlassCard, Header, Title, Subtitle } from '../style/layout';
import TopBar from '../components/TopBar';
import SettingsBars from '../components/SettingsBars';
import BiosenseSignalMonitor from '../components/BiosenseSignalMonitor';
import { useCameras, useDisableZoom } from '../hooks';
import UAParser from 'ua-parser-js';
import { StorageKeys } from '../utils/storage';

const CameraScanContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const TipsContainer = styled.div`
  margin: 0 auto;
  margin-top: 2rem;
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TipItem = styled.div`
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  text-align: left;
`;

const TipIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  margin-right: 16px;
  flex-shrink: 0;
  
  svg {
    width: 20px;
    height: 20px;
    fill: #ffffff;
  }
`;

const TipContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const TipTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 2px;
`;

const TipDescription = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.4;
`;

const CameraScan: React.FC = () => {
    const cameras = useCameras();
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isLicenseValid, setIsLicenseValid] = useState(false);
    const [cameraId, setCameraId] = useState<string>();

    // Logic from App.tsx handling defaults
    React.useEffect(() => {
        if (!cameras?.length) return;
        setCameraId(cameras[0].deviceId);
    }, [cameras]);

    const isMobile = useMemo(() => UAParser(navigator.userAgent).device.type === 'mobile', []);

    useDisableZoom();

    const toggleSettings = useCallback(() => {
        setIsSettingsOpen((prev) => !prev);
    }, []);

    const handleCloseSettings = useCallback(({ cameraId }: { cameraId?: string } = {}) => {
        if (cameraId) setCameraId(cameraId);
        setIsSettingsOpen(false);
    }, []);

    const updateLicenseStatus = useCallback((valid: boolean) => {
        setIsLicenseValid(valid);
    }, []);

    return (
        <PageContainer>
            <TopBar onSettingsClick={toggleSettings} isMeasuring={false} />

            <SettingsBars
                open={isSettingsOpen}
                onClose={handleCloseSettings}
                cameras={cameras}
                isLicenseValid={isLicenseValid}
            />

            <ContentWrapper>
                <Header>
                    <Title>Vital Health Scan</Title>
                    <Subtitle>Align your face within the frame and stay still for an accurate health assessment</Subtitle>
                </Header>

                <GlassCard>
                    <CameraScanContent>
                        <BiosenseSignalMonitor
                            showMonitor={!(isMobile && isSettingsOpen)}
                            cameraId={cameraId}
                            onLicenseStatus={updateLicenseStatus}
                            onSettingsClick={toggleSettings}
                            isSettingsOpen={isSettingsOpen}
                        />
                    </CameraScanContent>
                </GlassCard>

                <TipsContainer style={{ marginTop: '1.5rem', marginBottom: '2rem' }}>
                    <TipItem>
                        <TipIcon>
                            <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>
                        </TipIcon>
                        <TipContent>
                            <TipTitle>Assessment Guidelines</TipTitle>
                            <TipDescription>Stay relaxed, hold your phone at eye level with good lighting. The scan takes approximately 60 seconds.</TipDescription>
                        </TipContent>
                    </TipItem>
                </TipsContainer>
            </ContentWrapper>
        </PageContainer>
    );
};


export default CameraScan;
